import { useCallback } from 'react';
import { CommunicationMode } from '@wix/ambassador-genie-chat-service-v1-no-entity/types';
import { sendMessage } from '@wix/ambassador-genie-chat-service-v1-no-entity/http';
import { mapAssistantReference } from '../../../../mapAssistantReference';
import { isUserMessage } from '../../../../utils/domain/messageUtils';
import { SendMessageError } from '../errors';
import { useGetUserMessage } from './useGetUserMessage';
import { useMessagePolling } from './useMessagePolling';
import { useParseMessageResponse } from './useParseMessageResponse';
const safeError = error => error instanceof Error ? error : new Error(JSON.stringify(error));
export const useSendUserMessage = (httpClient, reference, enableLogging, enablePolling, toolRegistry) => {
  const communicationMode = enablePolling ? CommunicationMode.POLLING : CommunicationMode.FULL_RESPONSE;
  const parseMessageResponse = useParseMessageResponse();
  const sendUserMessageInternal = useCallback(async (message, conversationId, _toAgent, metadata) => {
    let data;
    try {
      const request = {
        userMessageId: message.id,
        functionCallResults: typeof message.content !== 'string' ? [message] : undefined,
        conversationId,
        enableLogging,
        metadata,
        assistantConfig: mapAssistantReference(reference),
        communicationMode
      };
      const res = await httpClient.request(sendMessage(request));
      data = {
        ...res.data,
        requestId: res.requestId
      };
    } catch (error) {
      const err = error;
      throw new SendMessageError('send-message-error', err.requestId ?? '', err.response);
    }
    return {
      messageResponse: parseMessageResponse(data),
      rawResponse: data
    };
  }, [parseMessageResponse, enableLogging, reference, communicationMode, httpClient]);
  const sendFunctionCallResultInternal = useCallback(async (functionCallResult, conversationId, _toAgent, metadata) => {
    let data;
    try {
      const request = {
        functionCallResults: [functionCallResult],
        conversationId,
        enableLogging,
        communicationMode,
        assistantConfig: mapAssistantReference(reference),
        metadata
      };
      const res = await httpClient.request(sendMessage(request));
      data = {
        ...res.data,
        requestId: res.requestId
      };
    } catch (error) {
      const err = error;
      throw new SendMessageError('send-message-error', err.requestId ?? '', err.response);
    }
    return {
      messageResponse: parseMessageResponse(data),
      rawResponse: data
    };
  }, [parseMessageResponse, enableLogging, communicationMode, reference, httpClient]);
  const executeTool = useCallback(async (toolId, request, params, toolSequence) => {
    const tool = await toolRegistry.getTool(toolId);
    if (!tool) {
      return {
        success: false,
        content: `tool '${toolId}' not found and cannot be executed`
      };
    }
    let result;
    try {
      tool.beforeExecute == null || tool.beforeExecute(params, request);
      result = await tool.execute(params, (toolSequence == null ? void 0 : toolSequence.payload) ?? undefined);
    } catch (error) {
      result = {
        success: false,
        content: `tool '${toolId}' execution failed: ${safeError(error)}`
      };
    }
    if (toolSequence != null && toolSequence.nextStep) {
      const payloadObj = JSON.parse(result.widgetResponse || '{}');
      result.widgetResponse = JSON.stringify({
        ...params,
        toolSequence: {
          currentStep: toolSequence.nextStep,
          payload: payloadObj
        }
      });
    }
    return result;
  }, [toolRegistry]);
  const getConversationMessageInternal = useGetUserMessage(httpClient);
  const sendMessageWithPolling = useMessagePolling(sendUserMessageInternal, getConversationMessageInternal);
  const sendFunctionCallResultWithPolling = useMessagePolling(sendFunctionCallResultInternal, getConversationMessageInternal);
  const sendUserMessage = enablePolling ? sendMessageWithPolling : sendUserMessageInternal;
  const sendFunctionCallResult = enablePolling ? sendFunctionCallResultWithPolling : sendFunctionCallResultInternal;
  return useCallback(async _ref => {
    let {
      message,
      conversationId,
      agentName,
      metadata
    } = _ref;
    if (!isUserMessage(message)) {
      throw new SendMessageError(`Invalid message role: ${message.role}. Only 'user' messages are allowed.`, '', undefined);
    }
    const requestStartTime = Date.now();
    let {
      messageResponse: _response
    } = await sendUserMessage(message, conversationId, agentName, metadata);

    // eslint-disable-next-line no-constant-condition
    while ('functionCall' in _response) {
      const functionCallIdentity = {
        id: _response.functionCall.id ?? '',
        name: _response.functionCall.name ?? ''
      };
      const {
        toolSequence,
        ...params
      } = JSON.parse(_response.functionCall.arguments || '{}');
      const toolId = (toolSequence == null ? void 0 : toolSequence.currentStep) || _response.functionCall.name || 'NO-TOOL';
      const toolExecutionResult = await executeTool(toolId, {
        requestId: _response.requestId,
        responseTimeMs: Date.now() - requestStartTime,
        logs: _response.logs
      }, params, toolSequence);
      const functionCallResult = {
        ...toolExecutionResult,
        ...functionCallIdentity
      };
      const functionCallResponse = await sendFunctionCallResult(functionCallResult, conversationId, _response.fromAgent, metadata);
      _response = functionCallResponse.messageResponse;
    }
    return {
      id: _response.id,
      createdDate: _response.createdDate,
      content: _response.content,
      requestId: _response.requestId,
      logs: _response.logs
    };
  }, [sendUserMessage, executeTool, sendFunctionCallResult]);
};
//# sourceMappingURL=useSendUserMessage.js.map